import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sprout, Wheat, TreeDeciduous, Leaf, Sun, Droplets, Users, BookOpen, Calendar, MapPin } from 'lucide-react';
import NavigationHeader from '../homepage/components/NavigationHeader';
import FooterSection from '../homepage/components/FooterSection';

export default function AgricultureShowcase() {
  const [selectedSeason, setSelectedSeason] = useState('all');

  const sustainablePractices = [
  {
    id: 1,
    title: "Organic Rice Terraces",
    icon: Wheat,
    description: "Traditional Palawan rice terracing using organic farming methods passed down through generations",
    image: "https://images.unsplash.com/photo-1677148469082-2deea29946ad",
    alt: "Lush green terraced rice paddies cascading down hillsides with water-filled fields reflecting sky, traditional farming structure visible in background",
    impact: "Zero chemical pesticides, 30% water conservation",
    season: "wet"
  },
  {
    id: 2,
    title: "Agroforestry Systems",
    icon: TreeDeciduous,
    description: "Integrated tree-crop farming combining native species with sustainable agriculture",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1134835b5-1764764836443.png",
    alt: "Mixed agricultural landscape showing tall tropical trees interspersed with crop rows, biodiversity-friendly farming system with various plant heights and species",
    impact: "50% biodiversity increase, carbon sequestration",
    season: "all"
  },
  {
    id: 3,
    title: "Traditional Root Crops",
    icon: Sprout,
    description: "Indigenous cassava and sweet potato cultivation preserving cultural agricultural heritage",
    image: "https://images.unsplash.com/photo-1554695651-f5f77ba837d6",
    alt: "Hands holding freshly harvested sweet potatoes and cassava roots with rich brown soil, traditional Filipino root vegetables displayed on woven basket",
    impact: "Drought-resistant, nutritional security",
    season: "dry"
  },
  {
    id: 4,
    title: "Permaculture Gardens",
    icon: Leaf,
    description: "Self-sustaining food forests mimicking natural ecosystems within protected zones",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19679df33-1765175684733.png",
    alt: "Dense permaculture garden with multiple layers of edible plants, herbs growing alongside vegetables in natural companion planting arrangement",
    impact: "Water-efficient, pesticide-free production",
    season: "all"
  }];


  const conservationTechniques = [
  {
    title: "Crop Rotation",
    description: "Strategic rotation of rice, legumes, and vegetables to maintain soil health",
    icon: Sun
  },
  {
    title: "Water Harvesting",
    description: "Traditional rainwater collection systems for irrigation during dry seasons",
    icon: Droplets
  },
  {
    title: "Community Farming",
    description: "Collaborative agricultural practices supporting local food security",
    icon: Users
  },
  {
    title: "Indigenous Knowledge",
    description: "Integration of traditional Filipino farming wisdom with modern conservation",
    icon: BookOpen
  }];


  const seasonalCalendar = [
  { month: "Jan-Mar", activity: "Rice Planting Season", type: "planting" },
  { month: "Apr-Jun", activity: "Vegetable Growth Peak", type: "growing" },
  { month: "Jul-Sep", activity: "Rice Harvest Festival", type: "harvest" },
  { month: "Oct-Dec", activity: "Root Crop Cultivation", type: "planting" }];


  const filteredPractices = selectedSeason === 'all' ?
  sustainablePractices :
  sustainablePractices?.filter((p) => p?.season === selectedSeason || p?.season === 'all');

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800">
      <NavigationHeader />
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden mt-20">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_10b9773e1-1764831765435.png"
          alt="Panoramic view of sustainable agricultural fields with terraced farming, green crops growing in organized rows, mountain landscape backdrop showing integration of conservation and traditional farming"
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
          <div className="max-w-7xl mx-auto px-4 h-full flex flex-col justify-end pb-16">
            <Link to="/site-showcase-gallery" className="flex items-center space-x-2 text-white/90 hover:text-white mb-4 w-fit">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Site Showcase</span>
            </Link>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Sustainable Agriculture
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Preserving traditional farming practices while protecting biodiversity within the national park buffer zones
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Season Filter */}
        <div className="flex justify-center space-x-4 mb-12">
          {['all', 'wet', 'dry']?.map((season) =>
          <button
            key={season}
            onClick={() => setSelectedSeason(season)}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
            selectedSeason === season ?
            'bg-emerald-600 text-white shadow-lg' :
            'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-600'}`
            }>

              {season === 'all' ? 'All Seasons' : season === 'wet' ? 'Wet Season' : 'Dry Season'}
            </button>
          )}
        </div>

        {/* Sustainable Practices Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {filteredPractices?.map((practice) =>
          <div key={practice?.id} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all group">
              <div className="relative h-64 overflow-hidden">
                <img
                src={practice?.image}
                alt={practice?.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 right-4 bg-emerald-600 text-white p-3 rounded-full">
                  <practice.icon className="h-6 w-6" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-3">
                  {practice?.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  {practice?.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-emerald-600 dark:text-emerald-400 font-semibold">
                    {practice?.impact}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {practice?.season === 'all' ? 'Year-round' : practice?.season === 'wet' ? 'Wet Season' : 'Dry Season'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conservation Techniques */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">
            Conservation Agriculture Techniques
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conservationTechniques?.map((technique, index) =>
            <div key={index} className="text-center p-6 rounded-xl bg-emerald-50 dark:bg-gray-700 hover:bg-emerald-100 dark:hover:bg-gray-600 transition-colors">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600 text-white rounded-full mb-4">
                  <technique.icon className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                  {technique?.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {technique?.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Seasonal Calendar */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl p-8 shadow-xl text-white">
          <div className="flex items-center justify-center space-x-3 mb-8">
            <Calendar className="h-8 w-8" />
            <h2 className="text-3xl font-bold">Agricultural Calendar</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {seasonalCalendar?.map((period, index) =>
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-colors">
                <div className="flex items-center space-x-2 mb-3">
                  <MapPin className="h-5 w-5" />
                  <span className="font-semibold text-lg">{period?.month}</span>
                </div>
                <p className="text-white/90">{period?.activity}</p>
                <span className="inline-block mt-3 px-3 py-1 bg-white/20 rounded-full text-xs uppercase tracking-wide">
                  {period?.type}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Educational Resources */}
        <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 text-center">
            Learn Traditional Farming
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors">
              <BookOpen className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Guided Tours</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Visit working farms with local experts</p>
            </div>
            <div className="text-center p-6 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors">
              <Users className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Workshops</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Hands-on sustainable farming sessions</p>
            </div>
            <div className="text-center p-6 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors">
              <Leaf className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Volunteer Programs</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Participate in conservation agriculture</p>
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
    </div>);

}